/*
  LP cart helpers, loaded site-wide with the cart drawer and on the cart page.

  Free gift contract (any buy box can use it):
  - A free gift line has the properties `_lp_free_gift` and `_lp_offer_id`.
  - The paid line it came with has the same `_lp_offer_id` and no `_lp_free_gift`.
  - When no paid line with that `_lp_offer_id` is left, the gift lines are removed.

  The cleanup does not depend on which code changed the cart or how the page re-renders:
  it runs on page load and after any cart/add|change|update|clear request on the page
  (theme, apps or other scripts), seen through the browser's resource timing entries,
  so nothing wraps window.fetch. It reads /cart.js, removes orphaned gift lines, then
  refreshes the cart drawer, or reloads the cart page.

  Cost: it only reads /cart.js while the cart may hold gift lines (layout/theme.liquid
  passes data-has-gifts at render time; any add re-arms it), so shoppers without LP gifts
  get no extra requests.

  LpCart.refreshDrawer: redraws the cart drawer and icon one section at a time (the
  theme's renderContent throws when Shopify returns null for a section).
*/
(() => {
  if (window.LpCart) return;
  // Set at render time by layout/theme.liquid: does the cart hold any _lp_free_gift lines?
  let mayHaveGifts = document.currentScript?.dataset.hasGifts !== 'false';
  const root = () => window.Shopify?.routes?.root || '/';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;

  // Fallback when the header section can't be rendered: set the cart icon count directly.
  async function updateCartCount(count) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    try {
      if (typeof count !== 'number') count = (await (await fetch(`${root()}cart.js`)).json()).item_count;
      let countEl = bubble.querySelector('.cart-count-bubble');
      if (count === 0) {
        countEl?.remove();
        return;
      }
      if (!countEl) {
        countEl = document.createElement('div');
        countEl.className = 'cart-count-bubble';
        countEl.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
        bubble.appendChild(countEl);
      }
      const visible = countEl.querySelector('[aria-hidden]');
      const hidden = countEl.querySelector('.visually-hidden');
      if (visible) visible.textContent = count < 100 ? count : '';
      if (hidden) hidden.textContent = hidden.textContent.replace(/\d+/, count) || `${count}`;
    } catch (error) {
      console.warn('[lp-cart] could not update the cart count', error);
    }
  }

  // Shopify sometimes returns null for a requested section (seen for the header, which holds
  // the cart icon). Re-fetch missing sections once, skip any that still fail.
  async function refreshDrawer(cartDrawer, sections = {}) {
    const targets = cartDrawer?.getSectionsToRender?.() || [];
    const missing = targets.filter((t) => !sections[t.section]).map((t) => t.section);
    if (missing.length) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', missing.join(','));
        const response = await fetch(url);
        if (response.ok) Object.assign(sections, await response.json());
      } catch (error) {
        console.warn('[lp-cart] could not re-fetch cart sections', missing, error);
      }
    }
    let bubbleStale = false;
    targets.forEach((t) => {
      const target = document.getElementById(t.id);
      const html = sections[t.section];
      const source = html && new DOMParser().parseFromString(html, 'text/html').querySelector(t.selector);
      if (target && source) target.innerHTML = source.innerHTML;
      else if (t.id === 'cart-icon-bubble') bubbleStale = true;
    });
    if (bubbleStale) await updateCartCount();
  }

  // Keys of gift lines whose paid line is no longer in the cart.
  function orphanedGiftKeys(cart) {
    const paid = new Set();
    cart.items.forEach((item) => {
      const props = item.properties || {};
      if (!props._lp_free_gift && props._lp_offer_id) paid.add(props._lp_offer_id);
    });
    return cart.items
      .filter((item) => {
        const props = item.properties || {};
        return props._lp_free_gift && props._lp_offer_id && !paid.has(props._lp_offer_id);
      })
      .map((item) => item.key);
  }

  let running = false;
  let queued = false;
  // Each line key is removed at most once per browser session, so a gift that something
  // re-adds can't cause a remove/reload loop (the cart page reloads after a removal).
  const TRIED_KEY = 'lp-cart:removed-gift-keys';
  const tried = new Set();
  try {
    JSON.parse(sessionStorage.getItem(TRIED_KEY) || '[]').forEach((key) => tried.add(key));
  } catch (error) {}
  const remember = (keys) => {
    keys.forEach((key) => tried.add(key));
    try {
      sessionStorage.setItem(TRIED_KEY, JSON.stringify([...tried].slice(-50)));
    } catch (error) {}
  };

  async function removeOrphanedGifts() {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    try {
      const cart = await (await fetch(`${root()}cart.js`, { cache: 'no-store' })).json();
      mayHaveGifts = cart.items.some((item) => item.properties?._lp_free_gift);
      const keys = orphanedGiftKeys(cart).filter((key) => !tried.has(key));
      if (!keys.length) return;
      remember(keys);

      const cartDrawer = document.querySelector('cart-drawer');
      const onCartPage = Boolean(document.getElementById('main-cart-items'));
      const sections = !onCartPage && cartDrawer?.getSectionsToRender?.().map((s) => s.section) || [];
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: Object.fromEntries(keys.map((key) => [key, 0])),
          sections,
          sections_url: window.location.pathname + window.location.search,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || response.statusText);
      document.dispatchEvent(new CustomEvent('lp:cart:gifts-removed', { detail: { keys } }));

      if (onCartPage) {
        window.location.reload();
        return;
      }
      if (cartDrawer) await refreshDrawer(cartDrawer, data.sections || {});
      else await updateCartCount(data.item_count);
    } catch (error) {
      console.warn('[lp-cart] could not remove free gifts without their paid line', error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        scheduleCheck();
      }
    }
  }

  let timer;
  function scheduleCheck() {
    clearTimeout(timer);
    // Let other scripts finish their own follow-up requests and re-renders first.
    timer = setTimeout(removeOrphanedGifts, 400);
  }

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return;
          const match = new URL(entry.name, window.location.href).pathname.match(CART_MUTATION);
          if (!match) return;
          if (match[1] === 'add') mayHaveGifts = true; // an add may bring gift lines
          if (mayHaveGifts) scheduleCheck();
        });
      }).observe({ type: 'resource', buffered: false });
    } catch (error) {
      console.warn('[lp-cart] cart change watcher unavailable', error);
    }
  }

  // A cart can be left with orphaned gifts by a change on another page or tab.
  const checkOnLoad = () => mayHaveGifts && scheduleCheck();
  if (document.readyState === 'complete') checkOnLoad();
  else window.addEventListener('load', checkOnLoad, { once: true });
  // Back/forward cache restores skip the render-time flag, so check whenever restored.
  window.addEventListener('pageshow', (event) => event.persisted && scheduleCheck());

  window.LpCart = { refreshDrawer, updateCartCount, removeOrphanedGifts };
})();
